import { EditOutlined } from '@ant-design/icons';
import { Button, Space, Switch, TablePaginationConfig } from 'antd';
import { FilterValue, SorterResult } from 'antd/es/table/interface';
import Typography from 'antd/es/typography/Typography';
import { Column, Table } from 'components';
import { StyledTableWrapper } from 'components/table/table.styled';
import dayjs from 'dayjs';
import { usePermissions, useUserContext } from 'features/users';
import { UserFiltersFormValuesPaginated } from 'features/users/components/users-filter-form';
import { useStatusChange } from 'features/users/use-cases/update-status';
import { Dispatch, SetStateAction, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { ROUTES } from 'routes/constants';
import { getRoute } from 'routes/utils';
import { User, Users } from 'types';

type UsersTableProps = {
  isUsersListLoading: boolean;
  usersList: any[];
  itemsCount: number;
  tableParams: any;
  updateParams: Dispatch<SetStateAction<UserFiltersFormValuesPaginated>>;
};

export const UsersTable = ({
  usersList,
  isUsersListLoading,
  // itemsCount,
  tableParams,
  updateParams,
}: UsersTableProps) => {
  const { t } = useTranslation();
  const permissions = usePermissions();
  const { user } = useUserContext();
  const navigate = useNavigate();
  const userId = localStorage.getItem('userId');
  const { mutate } = useStatusChange();
  const [loadingStatus, setLoadingStatus] = useState<{
    [key: string]: boolean;
  }>({});

  if (!permissions) return null;
  const handleTableChange = (
    pagination: TablePaginationConfig,
    filters: Record<string, FilterValue | null>,
    sorter: SorterResult<User> | SorterResult<User>[],
  ) => {
    updateParams({
      ...tableParams,
      PageIndex: pagination.current,
      RecordPerPage: pagination.pageSize,
      order_by: Array.isArray(sorter)
        ? undefined
        : sorter.order === 'ascend'
        ? 'name'
        : '-name',
    });
  };
  const handleStatusChange = (id: string, currentStatus: boolean) => {
    const updatedStatus = !currentStatus; // Toggle the current status
    const formData = {
      UserId: id,
      Status: updatedStatus, // Send the new status in the payload
      CompanyId: user?.CompanyId,
    };
    setLoadingStatus((prev) => ({ ...prev, [id]: true })); // Set loading state for the specific user
    mutate(formData, {
      onSuccess: () => {
        setLoadingStatus((prev) => ({ ...prev, [id]: false }));
        // Optional: Refresh or update local state here
      },
      onError: () => {
        setLoadingStatus((prev) => ({ ...prev, [id]: false }));
      },
    });
  };

  return (
    <StyledTableWrapper>
      {/* <StyledTableHeader></StyledTableHeader> */}
      <figure className='bg-white p-3'>
        <section className=''>
          <Table<User>
            loading={isUsersListLoading}
            dataSource={usersList}
            rowKey='id'
            onChange={handleTableChange}
            pagination={{
              current: tableParams.PageIndex,
              defaultPageSize: tableParams.RecordPerPage,
              total:
                usersList && usersList?.length > 0
                  ? usersList[0]?.TotalRecords
                  : 0,
            }}
            style={{ overflow: 'auto', whiteSpace: 'nowrap' }}
            onRow={(record) => {
              return {
                onClick: () => {
                  const selection = window?.getSelection()?.toString();

                  if (!selection?.length) {
                    navigate(getRoute(ROUTES.USERS_ADMIN_EDIT, record.id));
                  }
                },
              };
            }}
            components={{
              header: {
                cell: (
                  props: React.HTMLAttributes<HTMLTableHeaderCellElement>,
                ) => <th {...props} />,
              },
            }}
          >
            <Column
              title={'Sr No'}
              dataIndex={'Sr No'}
              render={(_, record, index) =>
                (tableParams.PageIndex - 1) * tableParams.RecordPerPage +
                index +
                1
              }
            />
            <Column<Users>
              title={t('usermanagement.table.username')}
              dataIndex={'Name'}
            />
            <Column<Users>
              title={t('usermanagement.table.Email')}
              dataIndex={'Email'}
            />
            <Column<Users>
              title={t('usermanagement.table.userrole')}
              dataIndex={'RoleName'}
            />
            <Column<Users>
              title={t('usermanagement.table.dateadded')}
              dataIndex={'CreatedDate'}
              render={(_, record: any) =>
                dayjs(record.CreatedDate).format('DD MMM YYYY')
              }
            />
            <Column<User>
              title={'Last Update'}
              dataIndex={'LastUpdated'}
              render={(_, record: any) =>
                dayjs(record.UpdateDate).format('DD MMM YYYY')
              }
            />
            <Column
              title='User Type'
              dataIndex='IsPrimary'
              render={(IsPrimary) => (
                <Typography>
                  {IsPrimary == false ? 'Staff' : 'Client'}
                </Typography>
              )}
            />
            <Column<Users>
              title={t('usermanagement.table.status')}
              dataIndex={'role'}
              render={(_, record) => (
                <Space>
                  <Typography
                    style={{
                      color: record.IsActive ? '#155724' : 'red', // Green for "Active", Red for "Inactive"
                    }}
                  >
                    {record.IsActive ? 'Active' : 'Inactive'}
                  </Typography>
                </Space>
              )}
            />
            <Column<User>
              title={t('usermanagement.table.action')}
              dataIndex={'Id'}
              render={(_, record: any) => (
                <Space>
                  {/* {record?.IsPrimary == false && ( */}
                  <Button
                    onClick={() =>
                      navigate(getRoute(ROUTES.USERS_ADMIN_EDIT, record.Id))
                    }
                  >
                    <EditOutlined />
                  </Button>
                  {/* )} */}
                  <Switch
                    disabled={record?.Id == userId} // Disable during loading
                    loading={loadingStatus[record.Id]} // Show loading for specific user
                    onChange={() =>
                      handleStatusChange(record.Id, record.IsActive)
                    } // Pass current status
                    checked={record.IsActive} // Reflect current status
                  />
                </Space>
              )}
            />
          </Table>
        </section>
      </figure>
    </StyledTableWrapper>
  );
};
